import { css, html } from "lit";
import { fallbackLabels, type LabelTable, type Labels } from "../../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressAudio, pressFeedback } from "../../chrome/ChromeElement";
import { artImage, artMask, type ChromeArt } from "../../chrome/chromeArt";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import { fitTexts } from "../../chrome/fitText";
import type { SpeedModePopupBindings, SpeedModePopupPort } from "./SpeedModePorts";
import { fitScale } from "@slot/presentation";

export const SPEED_MODE_POPUP_LABELS = { title: ["speed_mode.title", "Speed Mode"], turbo: ["speed_mode.turbo", "Fast"], superTurbo: ["speed_mode.super_turbo", "Super Fast"] } as const satisfies LabelTable;
export type SpeedModePopupLabels = Labels<typeof SPEED_MODE_POPUP_LABELS>;

/** The desktop popover (713.2×478.5) and the mobile overlay panel (848.1×564, centred 78.08 above the middle; max 1080 wide, 2000 in landscape). */
const DESKTOP = { width: 713.2, height: 478.5 } as const;
const MOBILE = { width: 848.1, height: 564, yUp: 78.08, maxWidth: 1080, landscapeMaxWidth: 2000, close: { size: 100, x: 321.6, yUp: 282 - 93.4 - 7.729 }, backdropAlpha: 200 / 255 } as const;
/** `SpeedModePopupContent.prefab`: the title top 50 (98.51 tall), the rows (600×100) at 0.276 above and 116.6 below the centre, x +5. */
const CONTENT = { titleTop: 50, titleHeight: 98.51, titleSize: 50, titleInset: 193.5, row: { width: 600, height: 100, x: 5, turboY: -0.276, superY: 116.6 }, icon: { x: -248.8, turbo: { width: 29.54, height: 43.46 }, superTurbo: { width: 27.92, height: 41.09 } }, label: { x: -135, size: 25 }, toggle: { x: 200, width: 121.4, height: 32.63, handle: 44.37, travel: 82 } } as const;

export const SPEED_POPUP_TAG = "slot-speed-popup";

/**
 * `<slot-speed-popup>`: the Fast / Super Fast picker. A popover above the desktop bar (a press
 * outside reports `onOutsideClick`); a full-screen overlay on mobile. Each row is one button with
 * a switch whose handle slides over 0.14 s.
 */
export class SpeedModePopupElement extends ChromeElement implements SpeedModePopupPort {
  public static override properties = {
    ...ChromeElement.properties,
    art: { attribute: false },
    labels: { attribute: false },
    open: { type: Boolean, reflect: true },
    turbo: { state: true },
    superTurbo: { state: true },
    turboShown: { state: true },
    superTurboShown: { state: true },
  };

  public declare art: ChromeArt;
  public declare labels: SpeedModePopupLabels;
  public declare open: boolean;
  /** Desktop: the toggle that opened the popover, excluded from outside-click dismissal. */
  public anchor: EventTarget | null = null;
  private declare turbo: boolean;
  private declare superTurbo: boolean;
  private declare turboShown: boolean;
  private declare superTurboShown: boolean;
  private bindings: SpeedModePopupBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.labels = fallbackLabels(SPEED_MODE_POPUP_LABELS);
    this.open = false;
    this.turbo = false;
    this.superTurbo = false;
    this.turboShown = true;
    this.superTurboShown = true;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: none; color: #fff; font-weight: 500; }
      :host([open]) { display: block; }
      .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${Number(MOBILE.backdropAlpha.toFixed(3))}); pointer-events: auto; }
      .panel { position: absolute; pointer-events: auto; }
      .close { position: absolute; width: ${MOBILE.close.size}px; height: ${MOBILE.close.size}px; left: calc(50% + ${MOBILE.close.x}px); top: calc(50% - ${MOBILE.close.yUp}px); transform: translate(-50%, -50%); }
      .close:hover { filter: brightness(0.39); }
      .close:active { filter: brightness(0.73); }
      .title { position: absolute; left: ${CONTENT.titleInset}px; right: ${CONTENT.titleInset}px; top: ${CONTENT.titleTop}px; height: ${CONTENT.titleHeight}px; display: flex; align-items: center; justify-content: center; font-size: ${CONTENT.titleSize}px; }
      .row { position: absolute; left: calc(50% + ${CONTENT.row.x}px); width: ${CONTENT.row.width}px; height: ${CONTENT.row.height}px; transform: translate(-50%, -50%); border-radius: 8px; background: #262626; text-align: left; }
      .row[hidden] { display: none; }
      .icon { position: absolute; top: 50%; left: calc(50% + ${CONTENT.icon.x}px); transform: translate(-50%, -50%); }
      .label { position: absolute; top: 50%; left: calc(50% + ${CONTENT.label.x}px); transform: translateY(-50%); font-size: ${CONTENT.label.size}px; white-space: nowrap; }
      .switch { position: absolute; top: 50%; left: calc(50% + ${CONTENT.toggle.x}px); width: ${CONTENT.toggle.width}px; height: ${CONTENT.toggle.height}px; transform: translate(-50%, -50%); }
      .switch .track { position: absolute; inset: 0; }
      .switch .handle { position: absolute; top: 50%; left: 50%; width: ${CONTENT.toggle.handle}px; height: ${CONTENT.toggle.handle}px; transform: translate(calc(-50% - ${CONTENT.toggle.travel / 2}px), -50%); transition: transform 0.14s linear; }
      .switch.on .handle { transform: translate(calc(-50% + ${CONTENT.toggle.travel / 2}px), -50%); }
    `,
  ];

  public bind(bindings: SpeedModePopupBindings): void {
    this.bindings = bindings;
  }

  public show(): void {
    this.open = true;
  }

  public hide(): void {
    this.open = false;
  }

  public setTurboEnabled(enabled: boolean): void {
    this.turbo = enabled;
  }

  public setSuperTurboEnabled(enabled: boolean): void {
    this.superTurbo = enabled;
  }

  public setTurboVisible(visible: boolean): void {
    this.turboShown = visible;
  }

  public setSuperTurboVisible(visible: boolean): void {
    this.superTurboShown = visible;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
    this.syncOutsideClick(this.open, this.anchor, () => this.bindings?.onOutsideClick());
  }

  private row(kind: "turbo" | "super", on: boolean, shown: boolean, label: string, y: number, onTap: () => void) {
    const icon = kind === "turbo"
      ? html`<div class="icon mask" style="width: ${CONTENT.icon.turbo.width}px; height: ${CONTENT.icon.turbo.height}px; ${artMask(this.art, "fastIcon", "#ffd220")}"></div>`
      : html`<div class="icon mask" style="width: ${CONTENT.icon.superTurbo.width}px; height: ${CONTENT.icon.superTurbo.height}px; left: calc(50% + ${CONTENT.icon.x + 0.9}px); ${artMask(this.art, "fastIcon", "#ff8500")}"></div>
        <div class="icon mask" style="width: ${CONTENT.icon.superTurbo.width}px; height: ${CONTENT.icon.superTurbo.height}px; left: calc(50% + ${CONTENT.icon.x + 0.9 - 15.39}px); ${artMask(this.art, "fastIcon", "#ffd220")}"></div>`;
    return html`<button class="row" type="button" data-part=${kind} ?hidden=${!shown} style="top: calc(50% + ${y}px)" @pointerdown=${pressFeedback} @click=${onTap}>
      ${icon}
      <div class="label">${label}</div>
      <div class="switch ${on ? "on" : ""}" data-part="${kind}-switch" data-on=${on}>
        <div class="track art" style=${artImage(this.art, on ? "switchOn" : "switchOff")}></div>
        <div class="handle art" style=${artImage(this.art, "switchHandle")}></div>
      </div>
    </button>`;
  }

  private content() {
    return html`
      <div class="title" data-part="title"><span data-fit>${this.labels.title}</span></div>
      ${this.row("turbo", this.turbo, this.turboShown, this.labels.turbo, CONTENT.row.turboY, () => this.bindings?.onTurboTap())}
      ${this.row("super", this.superTurbo, this.superTurboShown, this.labels.superTurbo, CONTENT.row.superY, () => this.bindings?.onSuperTurboTap())}
    `;
  }

  protected override render() {
    if (this.isDesktop) {
      return html`<div class="panel art" data-part="panel" style="left: 0; top: 0; width: ${DESKTOP.width}px; height: ${DESKTOP.height}px; ${artImage(this.art, "autoBetPanelBg")}">${this.content()}</div>`;
    }
    const v = this.view.design;
    const scale = fitScale(MOBILE, { maxWidth: this.isLandscape ? MOBILE.landscapeMaxWidth : MOBILE.maxWidth }, v);
    return html`
      <div class="backdrop"></div>
      <div class="panel art" data-part="panel" style="left: 50%; top: calc(50% - ${MOBILE.yUp}px); width: ${MOBILE.width}px; height: ${MOBILE.height}px; transform: translate(-50%, -50%) scale(${scale}); ${artImage(this.art, "autoBetPanelBg")}">
        ${this.content()}
        <button class="close art" type="button" data-part="close" style=${artImage(this.art, "closeRed")} @pointerdown=${pressAudio} @click=${() => this.bindings?.onClose()}></button>
      </div>
    `;
  }
}

export function defineSpeedModePopupElement(): void {
  defineChromeElement(SPEED_POPUP_TAG, SpeedModePopupElement);
}
